<template>
  <div>
    <!-- Les badges commandés : leur numéro une fois saisi, et s'ils sont activés -->
    <div class="text-caption text--secondary mb-1">{{ $t('onboarding.commissioning.badges') }}</div>
    <div v-if="options" class="text-body-2 mb-2">
      {{ $t(`onboarding.commissioning.badge-supply.${options.supply}`) }}
      · {{ $t(options.letter ? 'onboarding.commissioning.badge-letter-yes' : 'onboarding.commissioning.badge-letter-no') }}
      <template v-if="options.roaming"> · {{ $t(`onboarding.commissioning.badge-roaming.${options.roaming}`) }}</template>
    </div>
    <v-simple-table dense class="mb-3">
      <tbody>
        <tr v-for="kind in kinds" :key="kind">
          <td>{{ $t(`onboarding.commissioning.badge-supply.${kind}`) }}</td>
          <td>
            <span v-if="numberOf(kind)" class="font-weight-medium">{{ numberOf(kind) }}</span>
            <span v-else class="text--disabled">{{ $t('onboarding.order-panel.badge.not-entered') }}</span>
          </td>
          <td class="text-right">
            <v-chip v-if="isActivated(kind)" x-small color="green" text-color="white">{{ $t('onboarding.order-panel.badge.activated') }}</v-chip>
            <!-- Ce qu'on a remarqué à l'activation, sans rien bloquer -->
            <v-chip v-for="flag in flagsOf(kind)" :key="flag" x-small outlined color="orange" class="ml-1">{{ $t(`onboarding.order-panel.badge.flag.${flag}`) }}</v-chip>
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <!-- À envoyer : l'adresse de l'enveloppe, et la préparation sur sa page (saisie, courrier) -->
    <template v-if="order.step === 'shipment'">
      <div class="text-caption text--secondary">{{ $t('onboarding.queue.column.address') }}</div>
      <div class="mb-1" :class="{ 'error--text': !address }">{{ address || $t('onboarding.queue.no-address') }}</div>
      <v-chip v-if="order.shippingAddressChangedAt" x-small color="orange" text-color="white" class="mb-2">
        {{ $t('onboarding.badge-shipments.address-changed', { date: $options.filters.formatDate(order.shippingAddressChangedAt) }) }}
      </v-chip>
      <div v-if="heldCount" class="text-caption text--secondary mb-2">
        {{ $t('onboarding.queue.entry.held', { held: heldCount, total: kinds.length }) }} · {{ $t('onboarding.queue.entry.by', { by: order.badgesHeldBy || '-', date: $options.filters.formatDate(order.badgesHeldAt) }) }}
      </div>
      <div class="d-flex mt-2">
        <v-spacer />
        <v-btn small color="primary" :to="{ name: 'onboarding.badge-shipments.preparation', params: { id: order.id } }">
          <v-icon left small>mdi-package-variant</v-icon>{{ $t(heldCount ? 'onboarding.queue.action.resume' : 'onboarding.queue.action.prepare') }}
        </v-btn>
      </div>
    </template>

    <!-- À remettre : chaque badge saisi et validé, puis la remise -->
    <template v-else-if="order.step === 'handover'">
      <div v-if="heldCount" class="text-caption text--secondary mb-2">
        {{ $t('onboarding.queue.entry.held', { held: heldCount, total: kinds.length }) }} · {{ $t('onboarding.queue.entry.by', { by: order.badgesHeldBy || '-', date: $options.filters.formatDate(order.badgesHeldAt) }) }}
      </div>
      <div class="d-flex mt-2">
        <v-spacer />
        <v-btn small color="primary" @click="handing = true">
          <v-icon left small>mdi-card-account-details-outline</v-icon>{{ $t(heldCount ? 'onboarding.queue.action.resume' : 'onboarding.queue.action.hand-over') }}
        </v-btn>
      </div>
      <badge-designation-dialog :value="handing" :order="order" @input="handing = $event" @updated="$emit('updated', $event)" @done="$emit('changed')" />
    </template>

    <!-- À valider : un badge présenté hors stock par la personne ; ZEborne l'active pour elle, ou refuse en disant pourquoi -->
    <template v-else-if="order.step === 'badge-validation'">
      <div v-for="validation in pendingValidations" :key="validation.uid" class="order-badge-panel__validation mb-3">
        <div class="text-body-2">
          {{ $t('onboarding.order-panel.badge.validation', { uid: validation.uid, kind: $t(`onboarding.commissioning.badge-supply.${validation.kind}`), date: $options.filters.formatDate(validation.requestedAt) }) }}
        </div>
        <template v-if="currentUser && currentUser.isZE">
          <v-text-field
              v-if="refusing === validation.uid" v-model="reason" dense outlined hide-details="auto" class="mt-2"
              :label="$t('onboarding.order-panel.badge.refusal-reason')" autofocus
          />
          <div class="d-flex mt-2" style="gap: 8px">
            <v-spacer />
            <template v-if="refusing === validation.uid">
              <v-btn small text @click="refusing = null">{{ $t('cancel') }}</v-btn>
              <v-btn small color="error" :disabled="!reason.trim()" :loading="deciding === validation.uid" @click="decide(validation, false)">{{ $t('onboarding.order-panel.badge.refuse') }}</v-btn>
            </template>
            <template v-else>
              <v-btn small text color="error" @click="refusing = validation.uid; reason = ''">{{ $t('onboarding.order-panel.badge.refuse') }}</v-btn>
              <v-btn small color="primary" :loading="deciding === validation.uid" @click="decide(validation, true)">{{ $t('onboarding.order-panel.badge.validate') }}</v-btn>
            </template>
          </div>
        </template>
        <p v-else class="text-caption text--secondary mt-1 mb-0">{{ $t('onboarding.order-panel.badge.validation-by-zeborne') }}</p>
      </div>
      <v-alert v-if="error" type="error" dense text class="text-body-2">{{ error }}</v-alert>
    </template>

    <!-- À activer : partis ou remis, la personne les active en les recevant -->
    <p v-else-if="order.step === 'activation'" class="text-caption text--secondary mb-0">
      {{ $t('onboarding.order-panel.badge.awaiting-activation', { date: sentAt ? $options.filters.formatDate(sentAt) : '-' }) }}
    </p>
  </div>
</template>

<script>
import CommissioningRepository from '@repository/CommissioningRepository'
import CommissioningMixin from '../CommissioningMixin'
import BadgeDesignationDialog from '../Commissioning/BadgeDesignationDialog.vue'

/**
 * Une commande de badges dans le tiroir de la file : lesquels, leur numéro, et le geste de l'étape — valider un badge
 * présenté hors stock (ZEborne), préparer l'envoi (sur sa page : saisie, adresse, courrier), remettre en main propre (le
 * dialogue de remise), ou attendre que la personne active ses badges.
 */
export default {
  name: 'OrderBadgePanel',

  components: { BadgeDesignationDialog },

  mixins: [CommissioningMixin],

  props: {
    order: { type: Object, required: true },
  },

  data() {
    return { handing: false, refusing: null, reason: '', deciding: null, error: null }
  },

  computed: {
    options() {
      return this.order.options?.supply ? this.order.options : null
    },

    kinds() {
      const ordered = this.order.orderedBadgeKinds
      if (ordered && ordered.length) return ordered
      const supply = this.order.options?.supply || 'professional'
      return supply === 'both' ? ['professional', 'personal'] : [supply]
    },

    heldCount() {
      return Object.values(this.order.designatedBadges || {}).filter(Boolean).length
    },

    address() {
      return this.formatAddress(this.order.shippingAddress)
    },

    pendingValidations() {
      return (this.order.badgeValidations || []).filter(v => v.status === 'pending')
    },

    sentAt() {
      return (this.order.requirements || []).find(r => ['shipment', 'handover'].includes(r.requirement))?.satisfiedAt || null
    },
  },

  methods: {
    numberOf(kind) {
      return (this.order.designatedBadges || {})[kind] || null
    },

    /** Activé par la personne : la commande garde le badge par type. */
    isActivated(kind) {
      return !!(this.order.activatedBadges || {})[kind]
    },

    flagsOf(kind) {
      return (this.order.badgeFlags || {})[kind] || []
    },

    async decide(validation, accept) {
      this.deciding = validation.uid
      this.error = null
      try {
        const order = await CommissioningRepository.decideBadgeValidation(this.order.id, validation.uid, { accept, reason: accept ? null : this.reason.trim() })
        this.refusing = null
        this.$emit('updated', order)
        this.$emit('changed')
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.deciding = null
      }
    },
  },
}
</script>

<style scoped>
.order-badge-panel__validation {
  padding: 10px 12px;
  border: 1px solid rgba(255, 152, 0, 0.4);
  border-radius: 6px;
}
</style>
